import classnames from 'classnames/bind'
import { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { Children, Fragment, useEffect, type PropsWithChildren } from 'react'
import { PropsWithClassName } from '~/@types/generic'

import { EmblaSliderControls } from '~/components/UI/EmblaSlider/Controls'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type EmblaSliderProps = {
  containerClassName?: string
  controlsClassName?: string
  options?: EmblaOptionsType
  hideOverflow?: boolean
  sliderClassName?: string
  onSlideChange?: (index: number) => void
}

export function EmblaSlider({
  className,
  containerClassName,
  controlsClassName,
  children,
  options,
  hideOverflow = false,
  sliderClassName,
  onSlideChange,
}: PropsWithClassName<PropsWithChildren<EmblaSliderProps>>) {
  const [sliderRef, emblaApi] = useEmblaCarousel({
    ...options,
    skipSnaps: true,
  })

  const slidesCount = Children?.count?.(children)

  function onSliderChange(slider: EmblaCarouselType) {
    const activeIndex = slider?.selectedScrollSnap?.()
    onSlideChange?.(activeIndex)
  }

  useEffect(() => {
    if (!emblaApi) return
    onSliderChange(emblaApi)
    emblaApi.on('reInit', onSliderChange).on('select', onSliderChange)
  }, [emblaApi, onSliderChange])

  return (
    <div className={cx(className, css.EmblaSlider)}>
      <div
        ref={sliderRef}
        className={cx(
          containerClassName,
          css.container,
          hideOverflow && css.hideOverflow,
        )}>
        <div className={cx(sliderClassName, css.slides)}>{children}</div>
      </div>
      {slidesCount > 1 && (
        <EmblaSliderControls
          className={controlsClassName}
          emblaApi={emblaApi}
          length={slidesCount}
        />
      )}
    </div>
  )
}
